import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { lettersApi } from "@/api";
import type { LetterUnreadCount } from "@/types";
import { useAuth } from "@/staff/auth/AuthContext";

/**
 * App-level unread-letters count: the source for both the nav badge and the
 * inbox, so any mark-read/unread can invalidate it from anywhere. Polls
 * /unread-count every 60s while the tab is visible (paused when hidden, with an
 * immediate refresh on return). Keyed on the authed user's Xano id (auth/me
 * `id`) — if absent, it stays null and the badge simply hides.
 */

const POLL_MS = 60_000;

interface UnreadState {
  unread: LetterUnreadCount | null;
  refresh: () => Promise<void>;
}

const UnreadCtx = createContext<UnreadState>({ unread: null, refresh: async () => {} });

export function UnreadProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;
  const [unread, setUnread] = useState<LetterUnreadCount | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setUnread(null);
      return;
    }
    try {
      setUnread(await lettersApi.getUnreadCount(userId));
    } catch {
      /* keep the last good value rather than flicker to zero */
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setUnread(null);
      return;
    }
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (!interval) interval = setInterval(() => void refresh(), POLL_MS);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void refresh(); // instant on return
        start();
      }
    };

    void refresh();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId, refresh]);

  const value = useMemo<UnreadState>(() => ({ unread, refresh }), [unread, refresh]);
  return <UnreadCtx.Provider value={value}>{children}</UnreadCtx.Provider>;
}

export function useUnread(): UnreadState {
  return useContext(UnreadCtx);
}
